/**
 * useAgreementTerms.ts — resolving the booklet a Schedule's clause 3.1 names.
 *
 * The citation is parsed out of the frozen body (`agreement-terms-citation.ts`); this hook
 * turns it into the row, and refuses to hand back a row that is not the one the page names.
 *
 * ── THE DIGEST CHECK IS THE WHOLE POINT ───────────────────────────────────────
 * `(form_code, version)` is unique, so the lookup can only ever return one row — but
 * "unique" and "the booklet this signature covers" are different claims. The sixteen
 * characters printed on the page being signed are the ones the signer is attesting to, and a
 * row whose `body_sha256` does not begin with them is, by the page's own words, a different
 * document. It is treated as UNRESOLVED rather than shown with a warning: a booklet on screen
 * beside an acceptance tick is the app asserting that these are the Terms, and it must not
 * assert that on bytes the page contradicts.
 *
 * ── ABSENCE IS NOT AN ERROR, AND IT STILL HAPPENS ─────────────────────────────
 * A driver's SELECT on `agreement_terms` is filtered by a row policy this app does not
 * control, and the policy has been widened once already and may be again. Every outcome —
 * the table absent, a column absent, the policy filtering the row away, a genuine fault —
 * therefore resolves to null rather than throwing: the booklet is supporting material beside
 * a citation the page prints either way, and an expected read that returns nothing must not
 * become a Sentry event on every hire purchase ceremony, nor a ceremony a driver cannot
 * finish.
 *
 * ── RETIRED VERSIONS ARE READ, AND THE FILTER IS NOT DROPPED ──────────────────
 * Booklets republish, and a republish RETIRES the version before it. The signer of a Schedule
 * that cites v8 is entitled to v8 for the life of the instrument, so the status filter admits
 * `published` AND `retired`. It is not removed altogether: `(form_code, version)` is unique
 * only among rows that are not drafts, and a `.maybeSingle()` that could meet two rows is a
 * query that starts failing the day somebody drafts v9. Drafts stay out.
 *
 * `(supabase as any)` because `agreement_terms` is not in the generated types, as this app
 * does everywhere for the agreement subsystem.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { useQuery } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';
import { termsRowMatchesCitation } from '@/lib/agreement-terms-authority';
import type { TermsCitation } from '@/lib/agreement-terms-citation';

/** The statuses a signer's own cited booklet can legitimately be in. Never `draft`. */
export const READABLE_TERMS_STATUSES = ['published', 'retired'] as const;

/** One readable `agreement_terms` row, as the ceremony and the viewer read it. */
export interface AgreementTermsRow {
    id: number;
    terms_key: string;
    version: number;
    form_code: string;
    title: string;
    body_md: string;
    /** The full digest. The page prints its first sixteen characters; this is all of it. */
    body_sha256: string;
    /** `GENERATED ALWAYS` as `<form_code>/v<version>.pdf`. Never composed by this app. */
    storage_path: string;
    /**
     * `published` or `retired`.
     *
     * READ BECAUSE THE CEREMONY HAS TO BRANCH ON IT. `agreement_record_terms_accepted` accepts
     * a published version and refuses everything else, so a signer whose Schedule cites a
     * version that has since been retired would file a failure for a call that was never going
     * to succeed. The acceptance itself is not weakened by skipping it: the durable record is
     * the signature over clause 3.3 of the frozen body, and that RPC is a convenience.
     */
    status: string;
}

const TERMS_COLUMNS =
    'id, terms_key, version, form_code, title, body_md, body_sha256, storage_path, status';

/**
 * ── WHY THIS DOES NOT SPLIT ABSENCE FROM FAILURE, WHERE `useAgreements` DOES ──
 * That file keeps `isSubsystemAbsent` (PGRST202 / PGRST205) so a missing subsystem answers
 * empty while a REAL fault still throws — because there the fault is worth surfacing: a
 * Documents page that silently listed nothing would hide a driver's own agreements.
 *
 * Here the two collapse. The booklet is supporting material beside a citation the page prints
 * either way, so every outcome — table absent, column absent, policy filtering, a genuine
 * fault — lands the signer on the same screen showing the same citation. Splitting them would
 * buy a distinction with no consequence, and the branch that "still throws" would be a branch
 * that stops a signature over a PDF. What is NOT swallowed is a wrong booklet: the digest
 * check below is unconditional.
 */

/**
 * The booklet, or null — and null is a legitimate answer, not a failure state.
 *
 * A published version's bytes never change (publishing a new wording publishes a new
 * version), so the answer is cached for the session rather than revalidated.
 */
export function useAgreementTerms(citation: TermsCitation | null) {
    return useQuery({
        queryKey: ['agreement-terms', citation?.formCode ?? null, citation?.version ?? null],
        queryFn: async (): Promise<AgreementTermsRow | null> => {
            if (!citation) return null;

            const { data, error } = await (supabase as any)
                .from('agreement_terms')
                .select(TERMS_COLUMNS)
                .eq('form_code', citation.formCode)
                .eq('version', citation.version)
                .in('status', READABLE_TERMS_STATUSES)
                .maybeSingle();

            // PGRST202 / PGRST205 / anything else: one answer, for the reason above.
            if (error) return null;

            const row = (data ?? null) as AgreementTermsRow | null;
            if (!row) return null;

            // THE PAGE'S SIXTEEN CHARACTERS DECIDE. Not the lookup key, not the title, and —
            // where the viewer has one — not `agreement_templates.terms_id` either. See
            // `agreement-terms-authority.ts`, which is where that ruling lives.
            if (!termsRowMatchesCitation(row, citation)) return null;
            if (typeof row.body_md !== 'string' || row.body_md.trim().length === 0) return null;

            return row;
        },
        enabled: !!citation,
        staleTime: Infinity,
        retry: false,
    });
}
